'use client'

import { useEffect, useMemo, useCallback, useState, useRef, ReactNode } from 'react'
import { useFunnelConfig, preloadCategoryImages, ConfigProduct, ConfigCategory, ConfigStep, ColorFilter } from '@/lib/hooks/useFunnelConfig'
import { useConfiguratorStore } from '@/lib/store/configurator'
import { useConfiguratorAnalytics } from '@/lib/hooks/useAnalytics'
import { useWishlist } from '@/lib/hooks/useWishlist'
import { WishlistEmailPopup } from './WishlistEmailPopup'
import { WishlistSidebar } from './WishlistSidebar'
import { QuestionStep } from '@/types'
import { useSiteHeaderOffset } from '@/lib/siteHeader'
import { ct, translateConfig, cmoney } from '@/lib/i18n/configurator'

// Contexte transmis aux emplacements optionnels (encart « lot −20 % » du widget, voir LotUpsell)
export interface ConfiguratorSlotContext {
  selectedProducts: ConfigProduct[]
  totalPrice: number
  isValid: boolean
  baseWcId?: number | null
  baseName?: string
  baseImage?: string
  categories?: ConfigCategory[]
  size?: string
}

interface ConfiguratorProps {
  slug: string
  renderAfterSteps?: (ctx: ConfiguratorSlotContext) => ReactNode
  renderAboveBuy?: (ctx: ConfiguratorSlotContext) => ReactNode
  onAddToCart?: (products: ConfigProduct[], total: number, baseWcId?: number | null) => void
  cartLoading?: boolean
  cartAdded?: boolean
  mobileHeaderOffset?: number
}

// Parse config from URL path like /74-2-21-27/ or query param ?config=74-2-21-27
function parseConfigFromUrl(): number[] | null {
  if (typeof window === 'undefined') return null

  // Check query param first
  const params = new URLSearchParams(window.location.search)
  const configParam = params.get('config')
  if (configParam) {
    const ids = configParam.split('-').map(Number).filter(n => !isNaN(n) && n > 0)
    return ids.length > 0 ? ids : null
  }

  // Check path format /slug/74-2-21-27/
  const pathMatch = window.location.pathname.match(/\/(\d+(?:-\d+)*)\/?$/)
  if (pathMatch) {
    const ids = pathMatch[1].split('-').map(Number).filter(n => !isNaN(n) && n > 0)
    return ids.length > 0 ? ids : null
  }

  return null
}

// Preload all images and return a promise
function preloadAllImages(images: string[]): Promise<void[]> {
  return Promise.all(
    images.map(src => new Promise<void>((resolve) => {
      const img = new Image()
      img.onload = () => resolve()
      img.onerror = () => resolve() // Don't fail on error, just continue
      img.src = src
    }))
  )
}

export function Configurator({ slug, onAddToCart, cartLoading, cartAdded, renderAfterSteps, renderAboveBuy }: ConfiguratorProps) {
  const { config, isLoading, isError } = useFunnelConfig(slug)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  // L'en-tête du site se masque au défilement : l'aperçu collant (mobile) se cale juste dessous
  const headerOffset = useSiteHeaderOffset()
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [previewOverlay, setPreviewOverlay] = useState<{ image: string; name: string } | null>(null)
  const [selectedColorFilter, setSelectedColorFilter] = useState<string | null>(null) // tag
  const [mobileHeaderOffset, setMobileHeaderOffset] = useState(0) // Default 0 for widget
  const [colorFiltersEnabled, setColorFiltersEnabled] = useState(false) // Disabled by default

  // Load settings from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('configurator_settings')
    if (stored) {
      try {
        const settings = JSON.parse(stored)
        if (typeof settings.mobileHeaderOffset === 'number') {
          setMobileHeaderOffset(settings.mobileHeaderOffset)
        }
        if (typeof settings.colorFiltersEnabled === 'boolean') {
          setColorFiltersEnabled(settings.colorFiltersEnabled)
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [])

  // Store - subscribe to selections for reactivity
  const selections = useConfiguratorStore(state => state.selections)
  const questionAnswers = useConfiguratorStore(state => state.questionAnswers)
  const init = useConfiguratorStore(state => state.init)
  const selectProduct = useConfiguratorStore(state => state.selectProduct)
  const answerQuestion = useConfiguratorStore(state => state.answerQuestion)
  const resetStore = useConfiguratorStore(state => state.reset)
  const getVisibleCategories = useConfiguratorStore(state => state.getVisibleCategories)
  const getSelectedProductIds = useConfiguratorStore(state => state.getSelectedProductIds)

  // Analytics tracking
  const {
    startSession,
    trackInteraction,
    trackColorFilter,
    trackQuestionAnswer,
    endSession,
  } = useConfiguratorAnalytics(config?.id)

  // Wishlist (cross-funnel)
  const {
    email: wishlistEmail,
    wishlists,
    loading: wishlistLoading,
    showEmailPopup,
    setShowEmailPopup,
    showFullPopup,
    setShowFullPopup,
    requestEmail,
    submitEmail,
    addToWishlist,
    deleteWishlistItem,
    hasEmail: hasWishlistEmail,
    maxItems: wishlistMaxItems,
    isFull: wishlistIsFull,
  } = useWishlist(config?.id, slug)

  // Track if current config is saved in wishlist
  const [wishlistSaved, setWishlistSaved] = useState(false)

  // Mobile wishlist drawer
  const [mobileWishlistOpen, setMobileWishlistOpen] = useState(false)

  // Ref to measure mobile preview bottom position
  const mobilePreviewRef = useRef<HTMLDivElement>(null)
  const [previewBottom, setPreviewBottom] = useState(0)

  // Update preview bottom position on resize and scroll
  useEffect(() => {
    const updateBottom = () => {
      if (mobilePreviewRef.current) {
        setPreviewBottom(mobilePreviewRef.current.getBoundingClientRect().bottom)
      }
    }
    updateBottom()
    window.addEventListener('resize', updateBottom)
    window.addEventListener('scroll', updateBottom, { passive: true })
    return () => {
      window.removeEventListener('resize', updateBottom)
      window.removeEventListener('scroll', updateBottom)
    }
  }, [])

  // Track if we've restored from URL (to avoid overwriting with defaults)
  const urlRestoredRef = useRef(false)
  const isInitializedRef = useRef(false)

  // Sync selections to URL (using replaceState for performance - no re-render)
  useEffect(() => {
    if (!config || !isInitializedRef.current) return

    // Build config string from selected products' wcIds, ordered by category position
    const wcIds: number[] = []
    const categoryOrder = new Map<string, number>()

    // Build category position map from configSteps
    config.configSteps.forEach((step, idx) => {
      if (step.type === 'category' && step.categoryId) {
        categoryOrder.set(step.categoryId, idx)
      }
    })

    // Get wcIds in category order
    const sortedSelections = [...selections].sort((a, b) => {
      const posA = categoryOrder.get(a.categoryId) ?? 999
      const posB = categoryOrder.get(b.categoryId) ?? 999
      return posA - posB
    })

    sortedSelections.forEach(sel => {
      sel.productIds.forEach(pid => {
        const cat = config.categories.find(c => c.id === sel.categoryId)
        const product = cat?.products.find(p => p.id === pid)
        if (product?.wcId) {
          wcIds.push(product.wcId)
        }
      })
    })

    // Update URL with replaceState (no page reload, no history entry)
    const configString = wcIds.join('-')
    const newUrl = configString
      ? `${window.location.pathname}?config=${configString}`
      : window.location.pathname

    if (window.location.search !== `?config=${configString}`) {
      window.history.replaceState(null, '', newUrl)
    }
  }, [config, selections])

  // Handle color filter selection - auto-select first matching product in each visible category
  const handleColorFilterSelect = useCallback((tag: string | null) => {
    setSelectedColorFilter(tag)
    trackColorFilter(tag)

    if (tag && config) {
      // Get all visible category IDs (from direct steps + question-revealed categories)
      const visibleCategoryIds = new Set<string>()

      config.configSteps.forEach(step => {
        if (step.type === 'category' && step.categoryId) {
          visibleCategoryIds.add(step.categoryId)
        } else if (step.type === 'question' && step.id) {
          // Get the answer for this question
          const answer = questionAnswers[step.id]
          if (answer) {
            const categoriesToShow = answer === 'yes'
              ? step.optionYes?.showCategories
              : step.optionNo?.showCategories
            categoriesToShow?.forEach(catId => visibleCategoryIds.add(catId))
          }
        }
      })

      // For each visible category, select the first product that has this tag
      visibleCategoryIds.forEach(categoryId => {
        const category = config.categories.find(c => c.id === categoryId)
        if (category) {
          const matchingProduct = category.products.find(p => p.tags?.includes(tag))
          if (matchingProduct) {
            selectProduct(category.id, matchingProduct.id)
          }
        }
      })
    }
  }, [config, selectProduct, questionAnswers, trackColorFilter])

  // Initialize store when config loads
  useEffect(() => {
    if (config) {
      init({
        funnelId: config.id,
        size: config.size,
        categories: config.categories as any, // TODO: fix type
        configSteps: config.configSteps as any || [],
        baseProductId: '',
        baseImage: config.baseImage,
      })

      // Check for URL-based config to restore
      const urlWcIds = parseConfigFromUrl()

      if (urlWcIds && urlWcIds.length > 0) {
        // Restore from URL - build a map of wcId -> product for fast lookup
        const wcIdToProduct = new Map<number, { product: ConfigProduct; categoryId: string }>()
        config.categories.forEach(cat => {
          cat.products.forEach(p => {
            if (p.wcId) {
              wcIdToProduct.set(p.wcId, { product: p, categoryId: cat.id })
            }
          })
        })

        // Select products from URL
        const selectedCategoryIds = new Set<string>()
        urlWcIds.forEach(wcId => {
          const match = wcIdToProduct.get(wcId)
          if (match) {
            selectProduct(match.categoryId, match.product.id)
            selectedCategoryIds.add(match.categoryId)
          }
        })

        // For questions: determine answer based on which categories have selections
        config.configSteps.forEach(step => {
          if (step.type === 'question' && step.id) {
            const yesCategories = step.optionYes?.showCategories || []
            const noCategories = step.optionNo?.showCategories || []

            // Check if any "yes" category has a product selected
            const hasYesSelection = yesCategories.some(catId => selectedCategoryIds.has(catId))

            if (hasYesSelection) {
              answerQuestion(step.id, 'yes')
              // Auto-select first product in "yes" categories that don't have a selection yet
              yesCategories.forEach(catId => {
                if (!selectedCategoryIds.has(catId)) {
                  const category = config.categories.find(c => c.id === catId)
                  if (category && category.products.length > 0) {
                    selectProduct(category.id, category.products[0].id)
                  }
                }
              })
            } else {
              answerQuestion(step.id, 'no')
              // Auto-select first product in "no" categories that don't have a selection yet
              noCategories.forEach(catId => {
                if (!selectedCategoryIds.has(catId)) {
                  const category = config.categories.find(c => c.id === catId)
                  if (category && category.products.length > 0) {
                    selectProduct(category.id, category.products[0].id)
                  }
                }
              })
            }
          }
        })

        // Auto-select first product in direct categories that don't have a selection yet
        config.configSteps.forEach(step => {
          if (step.type === 'category' && step.categoryId) {
            if (!selectedCategoryIds.has(step.categoryId)) {
              const category = config.categories.find(c => c.id === step.categoryId)
              if (category && category.products.length > 0) {
                selectProduct(category.id, category.products[0].id)
              }
            }
          }
        })

        urlRestoredRef.current = true
      } else {
        // No URL config - use default behavior
        // Auto-select first option for questions and first product for revealed categories
        config.configSteps.forEach(step => {
          if (step.type === 'category') {
            // Auto-select first product in direct category steps
            const category = config.categories.find(c => c.id === step.categoryId)
            if (category && category.products.length > 0) {
              selectProduct(category.id, category.products[0].id)
            }
          } else if (step.type === 'question' && step.id) {
            // Auto-select "Non" for questions
            answerQuestion(step.id, 'no')

            // Auto-select first product in categories shown by "Non"
            step.optionNo?.showCategories?.forEach(catId => {
              const category = config.categories.find(c => c.id === catId)
              if (category && category.products.length > 0) {
                selectProduct(category.id, category.products[0].id)
              }
            })
          }
        })
      }

      // Mark as initialized for URL sync
      isInitializedRef.current = true

      // Start analytics session
      const urlWcIdsForAnalytics = parseConfigFromUrl()
      startSession(urlWcIdsForAnalytics || undefined)

      // Preload first category images
      if (config.categories.length > 0) {
        preloadCategoryImages(config.categories[0].products)
      }
    }
  }, [config, init, selectProduct, answerQuestion, startSession])

  // Get visible category IDs
  const visibleCategoryIds = getVisibleCategories()

  // Compute selected products with full data
  const selectedProducts = useMemo(() => {
    if (!config) return []
    const selectedIds = selections.flatMap(s => s.productIds)
    const products: ConfigProduct[] = []

    config.categories.forEach(cat => {
      cat.products.forEach(p => {
        if (selectedIds.includes(p.id)) {
          products.push(p)
        }
      })
    })

    return products.sort((a, b) => a.layer - b.layer)
  }, [config, selections])

  // Total price (base product + accessories)
  const totalPrice = useMemo(() => {
    const accessoriesPrice = selectedProducts.reduce((sum, p) => sum + p.price, 0)
    const basePrice = config?.basePrice || 0
    return basePrice + accessoriesPrice
  }, [config?.basePrice, selectedProducts])

  // Regular total price (without promo - for showing crossed-out price)
  const { regularTotalPrice, hasPromo } = useMemo(() => {
    const hasAnyPromo = selectedProducts.some(p => p.isOnSale && p.regularPrice)
    if (!hasAnyPromo) return { regularTotalPrice: 0, hasPromo: false }

    const regularAccessoriesPrice = selectedProducts.reduce((sum, p) => {
      // Use regularPrice if on sale, otherwise use price
      return sum + (p.isOnSale && p.regularPrice ? p.regularPrice : p.price)
    }, 0)
    const basePrice = config?.basePrice || 0
    return { regularTotalPrice: basePrice + regularAccessoriesPrice, hasPromo: true }
  }, [config?.basePrice, selectedProducts])

  // Check validity
  const isValid = useMemo(() => {
    if (!config) return false
    const visibleRequired = config.categories.filter(
      c => visibleCategoryIds.includes(c.id) && c.required
    )
    return visibleRequired.every(cat => {
      const sel = selections.find(s => s.categoryId === cat.id)
      return sel && sel.productIds.length > 0
    })
  }, [config, visibleCategoryIds, selections])

  const slotCtx: ConfiguratorSlotContext = { selectedProducts, totalPrice, isValid, baseWcId: config?.baseWcId, baseName: config?.baseName, baseImage: config?.baseImage, categories: config?.categories, size: config?.size }

  // Handlers
  const handleSelect = useCallback((categoryId: string, productId: string) => {
    selectProduct(categoryId, productId)
    trackInteraction(categoryId)
  }, [selectProduct, trackInteraction])

  const handleAnswerQuestion = useCallback((questionId: string, answer: 'yes' | 'no') => {
    answerQuestion(questionId, answer)
    trackQuestionAnswer(answer)

    // Auto-select first product in categories revealed by this answer
    // If there's a color filter active, select the first product matching that filter
    if (config) {
      const question = config.configSteps.find(
        s => s.type === 'question' && s.id === questionId
      )
      if (question && question.type === 'question') {
        const categoriesToShow = answer === 'yes'
          ? question.optionYes?.showCategories
          : question.optionNo?.showCategories

        categoriesToShow?.forEach(catId => {
          const category = config.categories.find(c => c.id === catId)
          if (category && category.products.length > 0) {
            // If color filter is active, prefer a product with that tag
            if (selectedColorFilter) {
              const matchingProduct = category.products.find(p => p.tags?.includes(selectedColorFilter))
              if (matchingProduct) {
                selectProduct(category.id, matchingProduct.id)
                return
              }
            }
            // Fallback to first product
            selectProduct(category.id, category.products[0].id)
          }
        })
      }
    }
  }, [answerQuestion, config, selectProduct, selectedColorFilter, trackQuestionAnswer])

  const handleAddToCart = useCallback(() => {
    if (onAddToCart && isValid) {
      // End analytics session with completion
      const wcIds = selectedProducts.map(p => p.wcId).filter(Boolean) as number[]
      endSession(true, wcIds, totalPrice)

      onAddToCart(selectedProducts, totalPrice, config?.baseWcId)
    }
  }, [onAddToCart, isValid, selectedProducts, totalPrice, endSession])

  // Handle wishlist heart click
  const handleWishlistClick = useCallback(async () => {
    if (!hasWishlistEmail) {
      // First click - request email
      requestEmail()
      return
    }

    // User has email, add to wishlist
    const wcIds = selectedProducts.map(p => p.wcId).filter(Boolean) as number[]
    const success = await addToWishlist(wcIds, totalPrice)
    if (success) {
      setWishlistSaved(true)
      // Reset saved indicator after 2s
      setTimeout(() => setWishlistSaved(false), 2000)
    }
  }, [hasWishlistEmail, requestEmail, addToWishlist, selectedProducts, totalPrice])

  // Handle email submission from popup
  const handleEmailSubmit = useCallback(async (email: string) => {
    submitEmail(email)
    // Auto-save after email is provided
    const wcIds = selectedProducts.map(p => p.wcId).filter(Boolean) as number[]
    const success = await addToWishlist(wcIds, totalPrice)
    if (success) {
      setWishlistSaved(true)
      setTimeout(() => setWishlistSaved(false), 2000)
    }
  }, [submitEmail, addToWishlist, selectedProducts, totalPrice])

  // Load config from wishlist (handles cross-funnel navigation)
  const handleLoadWishlistConfig = useCallback((wcIds: number[], funnelSlug?: string) => {
    // If from a different funnel, redirect to that configurator's page
    if (funnelSlug && funnelSlug !== slug) {
      const configString = wcIds.join('-')
      const pageUrl = config?.funnelUrls?.[funnelSlug]
      if (pageUrl) {
        window.location.href = `${pageUrl}?config=${configString}`
      } else {
        // Fallback to Next.js route if no page URL configured
        window.location.href = `/configurateur/${funnelSlug}?config=${configString}`
      }
      return
    }

    if (!config) return

    // Reset all selections first to avoid stacking
    resetStore()

    // Build a map of wcId -> product for fast lookup
    const wcIdToProduct = new Map<number, { product: ConfigProduct; categoryId: string }>()
    config.categories.forEach(cat => {
      cat.products.forEach(p => {
        if (p.wcId) {
          wcIdToProduct.set(p.wcId, { product: p, categoryId: cat.id })
        }
      })
    })

    // Determine which categories have products in this config
    const selectedCategoryIds = new Set<string>()
    wcIds.forEach(wcId => {
      const match = wcIdToProduct.get(wcId)
      if (match) {
        selectedCategoryIds.add(match.categoryId)
      }
    })

    // Set question answers based on which categories have products
    config.configSteps.forEach(step => {
      if (step.type === 'question' && step.id) {
        const yesCategories = step.optionYes?.showCategories || []
        const noCategories = step.optionNo?.showCategories || []

        // Check if any "yes" category has a product selected
        const hasYesSelection = yesCategories.some(catId => selectedCategoryIds.has(catId))

        if (hasYesSelection) {
          answerQuestion(step.id, 'yes')
        } else {
          answerQuestion(step.id, 'no')
        }
      }
    })

    // Select products from wishlist
    wcIds.forEach(wcId => {
      const match = wcIdToProduct.get(wcId)
      if (match) {
        selectProduct(match.categoryId, match.product.id)
      }
    })

    // Also auto-select first product in direct categories that don't have a selection
    config.configSteps.forEach(step => {
      if (step.type === 'category' && step.categoryId) {
        if (!selectedCategoryIds.has(step.categoryId)) {
          const category = config.categories.find(c => c.id === step.categoryId)
          if (category && category.products.length > 0) {
            selectProduct(category.id, category.products[0].id)
          }
        }
      }
    })
  }, [config, selectProduct, answerQuestion, resetStore, slug])

  // Get selection for a category
  const getSelection = useCallback((categoryId: string) => {
    return selections.find(s => s.categoryId === categoryId)?.productIds || []
  }, [selections])

  // All products for preloading in preview (must be before conditional returns)
  const allProducts = useMemo(() => {
    if (!config) return []
    return config.categories.flatMap(cat => cat.products)
  }, [config])

  // Textes affichés dans la langue de la page (mêmes identifiants : la logique garde la config d'origine)
  const view = useMemo(() => (config ? translateConfig(config) : config), [config])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="animate-spin w-8 h-8 border-4 border-gray-800 border-t-transparent rounded-full" />
      </div>
    )
  }

  // Error state
  if (isError || !config || !view) {
    return (
      <div className="flex items-center justify-center h-96 bg-red-50 rounded-lg">
        <p className="text-red-600">{ct('loadError')}</p>
      </div>
    )
  }

  // Get the selected product for a category (for display under title)
  const getSelectedProduct = (categoryId: string): ConfigProduct | null => {
    const selectedIds = getSelection(categoryId)
    if (selectedIds.length === 0) return null
    const cat = view.categories.find(c => c.id === categoryId)
    return cat?.products.find(p => p.id === selectedIds[0]) || null
  }

  return (
    <div className="configurator font-[family-name:var(--font-poppins)]">
      {/* Mobile: Sticky preview */}
      <div
        ref={mobilePreviewRef}
        className="lg:hidden sticky z-10 bg-white -mx-6 py-5"
        style={{ top: headerOffset }}
      >
        <Preview
          baseImage={config.baseImage}
          selectedProducts={selectedProducts}
          allProducts={allProducts}
          className="w-full aspect-[1200/711] rounded-none"
          onWishlistClick={handleWishlistClick}
          wishlistLoading={wishlistLoading}
          wishlistSaved={wishlistSaved}
          hasWishlistEmail={hasWishlistEmail}
          dimensions={view.dimensions}
          info={view.info}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[70%_30%] gap-8">
        {/* Desktop: Preview */}
        <div className="hidden lg:block">
          <div className="sticky top-8">
            {/* Color filters above preview - only when enabled in settings */}
            {colorFiltersEnabled && config.colorFilters && config.colorFilters.length > 0 && (
              <ColorFilterBar
                title={view.colorFilterTitle}
                filters={config.colorFilters}
                selectedTag={selectedColorFilter}
                onSelect={handleColorFilterSelect}
              />
            )}
            <Preview
              baseImage={config.baseImage}
              selectedProducts={selectedProducts}
              allProducts={allProducts}
              className="h-[700px]"
              onWishlistClick={handleWishlistClick}
              wishlistLoading={wishlistLoading}
              wishlistSaved={wishlistSaved}
              hasWishlistEmail={hasWishlistEmail}
              dimensions={view.dimensions}
              info={view.info}
            />
          </div>
        </div>

        {/* Controls - Fixed height on desktop with custom scrollbar */}
        <div className="flex flex-col lg:max-h-[700px]">
          {/* Scrollable config area */}
          <div className="flex-1 overflow-y-auto lg:pr-2 config-scroll">
            <div className="flex flex-col gap-6">
              {/* Render config steps in order */}
              {view.configSteps.map((step, index) => {
                if (step.type === 'category') {
                  const category = view.categories.find(c => c.id === step.categoryId)
                  if (!category) return null

                  return (
                    <CategorySection
                      key={step.categoryId}
                      category={category}
                      selectedIds={getSelection(category.id)}
                      selectedProduct={getSelectedProduct(category.id)}
                      onSelect={(productId) => handleSelect(category.id, productId)}
                      onPreview={(image, name) => setPreviewOverlay({ image, name })}
                      filterTag={selectedColorFilter}
                    />
                  )
                }

                if (step.type === 'question' && step.id) {
                  const answer = questionAnswers[step.id]

                  return (
                    <div key={step.id}>
                      <QuestionBlock
                        question={step as QuestionStep}
                        answer={answer}
                        onAnswer={(ans) => handleAnswerQuestion(step.id!, ans)}
                      />

                      {/* Show categories based on answer */}
                      {answer && (
                        <div className="mt-4 space-y-6">
                          {(answer === 'yes' ? step.optionYes?.showCategories : step.optionNo?.showCategories)?.map(catId => {
                            const category = view.categories.find(c => c.id === catId)
                            if (!category) return null

                            return (
                              <CategorySection
                                key={category.id}
                                category={category}
                                selectedIds={getSelection(category.id)}
                                selectedProduct={getSelectedProduct(category.id)}
                                onSelect={(productId) => handleSelect(category.id, productId)}
                                onPreview={(image, name) => setPreviewOverlay({ image, name })}
                                filterTag={selectedColorFilter}
                              />
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                }

                return null
              })}
              {renderAfterSteps?.(slotCtx)}
            </div>
          </div>

          {/* Footer with price and buy button - stays fixed at bottom */}
          <div data-fc-buybar className="sticky bottom-0 bg-white border-t border-gray-200 pt-4 pb-[10px] mt-4 -mx-4 px-4 lg:relative lg:border-0 lg:py-4 lg:mx-0 lg:px-0 lg:mt-4 flex-shrink-0">
            {renderAboveBuy?.(slotCtx)}
            <button
              onClick={handleAddToCart}
              disabled={!isValid || cartLoading || cartAdded}
              className={`w-full flex items-center justify-between py-2 px-6 rounded-[1px] font-medium text-lg transition-colors ${
                cartAdded
                  ? 'bg-green-600 text-white'
                  : isValid && !cartLoading
                    ? 'bg-[#171717] text-white hover:bg-[#2a2a2a]'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span>{cartLoading ? ct('adding') : cartAdded ? ct('added') : ct('buy')}</span>
              <span className="flex items-center gap-2">
                {hasPromo && regularTotalPrice > totalPrice && (
                  <span className="line-through text-gray-400 text-sm">
                    {cmoney(regularTotalPrice)}
                  </span>
                )}
                <span>{cmoney(totalPrice)}</span>
              </span>
            </button>

            {/* Mobile: Ma wishlist button */}
            {hasWishlistEmail && wishlists.length > 0 && (
              <button
                onClick={() => setMobileWishlistOpen(true)}
                className="lg:hidden w-full mt-2 py-2 px-4 text-sm text-pink-500 font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
                {ct('myWishlist')} ({wishlists.length}/{wishlistMaxItems})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Wishlist Email Popup */}
      {showEmailPopup && (
        <WishlistEmailPopup
          onSubmit={handleEmailSubmit}
          onClose={() => setShowEmailPopup(false)}
        />
      )}

      {/* Wishlist Full Popup */}
      {showFullPopup && (
        <WishlistFullPopup
          count={wishlists.length}
          max={wishlistMaxItems}
          onClose={() => setShowFullPopup(false)}
        />
      )}

      {/* Preview Overlay */}
      {previewOverlay && (
        <PreviewOverlay
          image={previewOverlay.image}
          name={previewOverlay.name}
          onClose={() => setPreviewOverlay(null)}
        />
      )}

      {/* Wishlist Sidebar - Desktop only */}
      <div className="hidden lg:block">
        <WishlistSidebar
          wishlists={wishlists}
          currentBaseImage={config.baseImage}
          onLoadConfig={handleLoadWishlistConfig}
          onDelete={deleteWishlistItem}
          maxItems={wishlistMaxItems}
        />
      </div>

      {/* Mobile Color Drawer - only when enabled in settings */}
      {colorFiltersEnabled && config.colorFilters && config.colorFilters.length > 0 && (
        <div className="lg:hidden">
          <MobileColorDrawer
            filters={config.colorFilters}
            selectedTag={selectedColorFilter}
            onSelect={handleColorFilterSelect}
          />
        </div>
      )}

      {/* Mobile Wishlist Drawer */}
      {mobileWishlistOpen && (
        <MobileWishlistDrawer
          wishlists={wishlists}
          currentBaseImage={config.baseImage}
          onLoadConfig={handleLoadWishlistConfig}
          onDelete={deleteWishlistItem}
          onClose={() => setMobileWishlistOpen(false)}
          previewBottom={previewBottom}
          maxItems={wishlistMaxItems}
        />
      )}
    </div>
  )
}

// ============================================
// PREVIEW COMPONENT
// ============================================
interface PreviewProps {
  baseImage?: string
  selectedProducts: ConfigProduct[]
  allProducts: ConfigProduct[] // All products for preloading
  className?: string
  // Wishlist props
  onWishlistClick?: () => void
  wishlistLoading?: boolean
  wishlistSaved?: boolean
  hasWishlistEmail?: boolean
  // Info drawer props
  dimensions?: string
  info?: string
}

function Preview({
  baseImage,
  selectedProducts,
  allProducts,
  className = '',
  onWishlistClick,
  wishlistLoading,
  wishlistSaved,
  hasWishlistEmail,
  dimensions,
  info,
}: PreviewProps) {
  const selectedIds = selectedProducts.map(p => p.id)
  const [infoOpen, setInfoOpen] = useState(false)
  const hasInfo = Boolean((dimensions && dimensions.trim()) || (info && info.trim()))

  return (
    <div className={`relative bg-white overflow-hidden ${className}`}>
      {/* Wishlist heart button - top left */}
      {onWishlistClick && (
        <button
          onClick={onWishlistClick}
          disabled={wishlistLoading}
          className="absolute -top-2 left-4 z-50 p-2 transition-all"
          title={hasWishlistEmail ? ct('saveSelection') : ct('createWishlist')}
        >
          <svg
            className={`w-7 h-7 transition-colors ${
              wishlistSaved
                ? 'text-pink-500 fill-pink-500'
                : 'text-pink-400 hover:text-pink-500'
            }`}
            viewBox="0 0 24 24"
            fill={wishlistSaved ? 'currentColor' : 'white'}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </button>
      )}

      {/* Dimensions & infos button - top right */}
      {hasInfo && (
        <button
          onClick={() => setInfoOpen(true)}
          className="absolute z-50 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
          style={{
            top: 0,
            right: '1rem',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            color: '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '9999px',
            boxShadow: '0 1px 2px rgba(0,0,0,.06)',
          }}
          title={ct('seeInfo')}
          aria-label={ct('infoAria')}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span className="hidden sm:inline">{ct('infoTitle')}</span>
        </button>
      )}

      {/* Base image */}
      {baseImage && (
        <img
          src={baseImage}
          alt="Base"
          className="absolute inset-0 w-full h-full object-contain"
          style={{ zIndex: 0 }}
        />
      )}

      {/* Pre-render ALL product images, show/hide based on selection */}
      {allProducts.map(product => product.image && (
        <img
          key={product.id}
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain"
          style={{
            zIndex: product.layer,
            opacity: selectedIds.includes(product.id) ? 1 : 0,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Placeholder if no base image */}
      {!baseImage && selectedProducts.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          <span className="text-4xl">👜</span>
        </div>
      )}

      {/* Dimensions & infos drawer (overlays the preview, slides from the right).
          Uses inline styles for transform/zIndex so it works inside the widget's
          Shadow DOM regardless of which Tailwind classes are forced. */}
      {hasInfo && (
        <>
          {/* Panel */}
          <div
            role="dialog"
            aria-label={ct('infoAria')}
            aria-hidden={!infoOpen}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 99961,
              width: '85%',
              maxWidth: '24rem',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,.15), 0 8px 10px -6px rgba(0,0,0,.1)',
              transform: infoOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 300ms ease-out',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 text-left">{ct('infoTitle')}</h3>
              <button
                onClick={() => setInfoOpen(false)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                aria-label={ct('close')}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {dimensions && dimensions.trim() && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1 text-left">{ct('dimensions')}</p>
                  <p className="text-sm text-gray-900 whitespace-pre-line text-left">{dimensions}</p>
                </div>
              )}
              {info && info.trim() && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1 text-left">{ct('infos')}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed text-left">{info}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ============================================
// CATEGORY SECTION COMPONENT
// ============================================
interface CategorySectionProps {
  category: ConfigCategory
  selectedIds: string[]
  selectedProduct: ConfigProduct | null
  onSelect: (productId: string) => void
  onPreview: (image: string, name: string) => void
  filterTag?: string | null
}

function CategorySection({ category, selectedIds, selectedProduct, onSelect, onPreview, filterTag }: CategorySectionProps) {
  // Filter products by color tag if one is selected
  const filteredProducts = filterTag
    ? category.products.filter(p => p.tags?.includes(filterTag))
    : category.products

  // If filter results in empty products, show all products (graceful fallback)
  const productsToShow = filteredProducts.length > 0 ? filteredProducts : category.products
  // List display mode
  if (category.displayMode === 'list') {
    return (
      <div className="category-section">
        {/* Header */}
        <div className="mb-2">
          <h3 className="font-medium text-gray-900 text-left">
            {category.name}
            {category.required && <span className="text-red-500 ml-1">*</span>}
          </h3>
        </div>

        {/* Products list */}
        <div className="flex flex-col gap-1">
          {productsToShow.map(product => {
            const isSelected = selectedIds.includes(product.id)

            return (
              <button
                key={product.id}
                onClick={() => onSelect(product.id)}
                className={`flex items-center gap-3 py-1.5 px-2 border-2 transition-colors bg-white text-gray-700 rounded-[3px] ${
                  isSelected
                    ? 'border-gray-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Thumbnail */}
                <div className="w-8 h-8 flex-shrink-0 overflow-hidden bg-gray-100 rounded-[2px]">
                  {product.thumb ? (
                    <img
                      src={product.thumb}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                      {product.name.substring(0, 2)}
                    </div>
                  )}
                </div>

                {/* Name and promo price */}
                <span className="flex-1 text-left text-sm font-medium flex items-center gap-2">
                  <span>{product.name}</span>
                  {product.isOnSale && product.regularPrice && (
                    <span className="flex items-center gap-1 text-xs font-normal">
                      <span className="line-through text-gray-400">
                        {(product.regularPrice / 100).toFixed(0)}€
                      </span>
                      <span className="text-[#9b2b2a] font-medium">
                        {(product.price / 100).toFixed(0)}€
                      </span>
                    </span>
                  )}
                </span>

                {/* Eye icon for preview */}
                {product.previewImage && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      onPreview(product.previewImage!, product.name)
                    }}
                    className="p-1 rounded-full transition-colors hover:bg-gray-100"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-400"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // Default grid display mode
  return (
    <div className="category-section">
      {/* Header */}
      <div className="mb-2">
        <h3 className="font-medium text-gray-900 text-left">
          {category.name}
          {category.required && <span className="text-red-500 ml-1">*</span>}
        </h3>
      </div>

      {/* Selected product name and promo price */}
      {selectedProduct && (
        <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
          <span>{selectedProduct.name}</span>
          {selectedProduct.isOnSale && selectedProduct.regularPrice && (
            <span className="flex items-center gap-1 text-xs">
              <span className="line-through text-gray-400">
                {(selectedProduct.regularPrice / 100).toFixed(0)}€
              </span>
              <span className="text-[#9b2b2a] font-medium">
                {(selectedProduct.price / 100).toFixed(0)}€
              </span>
            </span>
          )}
        </p>
      )}

      {/* Products grid */}
      <div className="grid grid-cols-5 gap-[2px]">
        {productsToShow.map(product => {
          const isSelected = selectedIds.includes(product.id)

          return (
            <button
              key={product.id}
              onClick={() => onSelect(product.id)}
              className={`relative overflow-hidden border p-[2px] bg-white transition-all aspect-square rounded-[3px] ${
                isSelected
                  ? 'border-gray-900'
                  : 'border-transparent'
              }`}
            >
              {product.thumb ? (
                <img
                  src={product.thumb}
                  alt={product.name}
                  className="w-full h-full object-cover rounded-[2px]"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs rounded-[2px]">
                  {product.name.substring(0, 2)}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// PREVIEW OVERLAY COMPONENT
// ============================================
interface PreviewOverlayProps {
  image: string
  name: string
  onClose: () => void
}

function PreviewOverlay({ image, name, onClose }: PreviewOverlayProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div className="relative max-w-2xl max-h-[90vh] mx-4">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Image */}
        <img
          src={image}
          alt={name}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Name */}
        <p className="text-white text-center mt-4 font-medium">{name}</p>
      </div>
    </div>
  )
}

// ============================================
// QUESTION BLOCK COMPONENT
// ============================================
interface QuestionBlockProps {
  question: QuestionStep
  answer: 'yes' | 'no' | undefined
  onAnswer: (answer: 'yes' | 'no') => void
}

export function QuestionBlock({ question, answer, onAnswer }: QuestionBlockProps) {
  return (
    <div className="question-block">
      {/* Question text */}
      <div className="mb-3">
        <h3 className="font-medium text-gray-900 text-left">
          {question.text}
          <span className="text-red-500 ml-1">*</span>
        </h3>
      </div>

      {/* Options */}
      <div className="flex gap-2">
        <button
          onClick={() => onAnswer('no')}
          className={`flex-1 py-1.5 px-2 text-xs font-medium border-2 transition-colors rounded-[3px] ${
            answer === 'no'
              ? 'bg-[#171717] text-white border-[#171717]'
              : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
          }`}
        >
          {question.optionNo.label}
        </button>
        <button
          onClick={() => onAnswer('yes')}
          className={`flex-1 py-1.5 px-2 text-xs font-medium border-2 transition-colors rounded-[3px] ${
            answer === 'yes'
              ? 'bg-[#171717] text-white border-[#171717]'
              : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
          }`}
        >
          {question.optionYes.label}
        </button>
      </div>
    </div>
  )
}

// ============================================
// COLOR FILTER BAR COMPONENT (Desktop)
// ============================================
interface ColorFilterBarProps {
  title?: string
  filters: ColorFilter[]
  selectedTag: string | null
  onSelect: (tag: string | null) => void
}

function ColorFilterBar({ title, filters, selectedTag, onSelect }: ColorFilterBarProps) {
  return (
    <div className="mb-4 text-center">
      {title && (
        <p className="text-sm text-gray-600 mb-3">{title}</p>
      )}
      <div className="flex items-center justify-center gap-3 flex-wrap">
        {/* "All" option */}
        <button
          onClick={() => onSelect(null)}
          className={`w-8 h-8 rounded-full border-2 transition-all flex items-center justify-center ${
            selectedTag === null
              ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-2'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          title={ct('all')}
        >
          <span className="text-xs text-gray-500">✕</span>
        </button>

        {filters.map(filter => (
          <button
            key={filter.id}
            onClick={() => onSelect(filter.tag)}
            className={`w-8 h-8 rounded-full border-2 transition-all overflow-hidden ${
              selectedTag === filter.tag
                ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-2'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            title={filter.label}
          >
            {filter.hex ? (
              <div
                className="w-full h-full"
                style={{ backgroundColor: filter.hex }}
              />
            ) : filter.image ? (
              <img
                src={filter.image}
                alt={filter.label}
                className="w-full h-full object-cover"
              />
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

// ============================================
// MOBILE COLOR DRAWER COMPONENT
// ============================================
interface MobileColorDrawerProps {
  filters: ColorFilter[]
  selectedTag: string | null
  onSelect: (tag: string | null) => void
}

function MobileColorDrawer({ filters, selectedTag, onSelect }: MobileColorDrawerProps) {
  const [isOpen, setIsOpen] = useState(false)

  // Get selected filter for display
  const selectedFilter = filters.find(f => f.tag === selectedTag)

  return (
    <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center">
      {/* Drawer panel */}
      <div
        className={`bg-white shadow-lg rounded-l-xl overflow-hidden transition-all duration-300 ${
          isOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
        }`}
      >
        <div className="flex items-center gap-2 p-3">
          {/* "All" option */}
          <button
            onClick={() => {
              onSelect(null)
              setIsOpen(false)
            }}
            className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center flex-shrink-0 ${
              selectedTag === null
                ? 'border-gray-900 bg-gray-100'
                : 'border-gray-300'
            }`}
          >
            <span className="text-sm text-gray-500">✕</span>
          </button>

          {filters.map(filter => (
            <button
              key={filter.id}
              onClick={() => {
                onSelect(filter.tag)
                setIsOpen(false)
              }}
              className={`w-10 h-10 rounded-full border-2 transition-all overflow-hidden flex-shrink-0 ${
                selectedTag === filter.tag
                  ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1'
                  : 'border-gray-300'
              }`}
            >
              {filter.hex ? (
                <div
                  className="w-full h-full"
                  style={{ backgroundColor: filter.hex }}
                />
              ) : filter.image ? (
                <img
                  src={filter.image}
                  alt={filter.label}
                  className="w-full h-full object-cover"
                />
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white shadow-lg rounded-l-xl p-3 flex items-center gap-2"
      >
        {/* Show selected color or palette icon */}
        {selectedFilter ? (
          <div
            className="w-8 h-8 rounded-full border-2 border-gray-300 overflow-hidden"
          >
            {selectedFilter.hex ? (
              <div
                className="w-full h-full"
                style={{ backgroundColor: selectedFilter.hex }}
              />
            ) : selectedFilter.image ? (
              <img
                src={selectedFilter.image}
                alt={selectedFilter.label}
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
        ) : (
          <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
        )}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </div>
  )
}

// ============================================
// MOBILE WISHLIST DRAWER COMPONENT
// ============================================
interface MobileWishlistDrawerProps {
  wishlists: { id: string; config: number[]; total_price: number; funnel_slug?: string; base_image?: string; product_images?: { wcId: number; image: string; layer: number }[]; has_unavailable_products?: boolean }[]
  currentBaseImage?: string
  onLoadConfig: (wcIds: number[], funnelSlug?: string) => void
  onDelete: (id: string) => void
  onClose: () => void
  previewBottom: number
  maxItems: number
}

function MobileWishlistDrawer({
  wishlists,
  currentBaseImage,
  onLoadConfig,
  onDelete,
  onClose,
  previewBottom,
  maxItems,
}: MobileWishlistDrawerProps) {
  // Split wishlists into available and unavailable
  const availableWishlists = wishlists.filter(w => !w.has_unavailable_products)
  const unavailableWishlists = wishlists.filter(w => w.has_unavailable_products)

  // Use measured preview bottom position directly
  const topPosition = `${previewBottom}px`

  const renderItem = (item: typeof wishlists[0], isUnavailable: boolean) => {
    const itemBaseImage = item.base_image || currentBaseImage
    // Use product_images from the API (already in the correct size for this wishlist)
    const productImages = item.product_images || []

    return (
      <div key={item.id} className="relative">
        {/* Mini preview */}
        <div
          onClick={() => !isUnavailable && onLoadConfig(item.config, item.funnel_slug)}
          className={`w-full aspect-square bg-gray-100 rounded-lg overflow-hidden relative border-2 border-transparent ${
            isUnavailable ? '' : 'hover:border-pink-400 cursor-pointer'
          } transition-colors`}
        >
          {/* Base image (from the item's funnel) */}
          {itemBaseImage && (
            <img
              src={itemBaseImage}
              alt=""
              className={`absolute inset-0 w-full h-full object-contain ${isUnavailable ? 'opacity-40' : ''}`}
            />
          )}
          {/* Product layers (using pre-fetched images in the correct size) */}
          {productImages.map(p => p.image && (
            <img
              key={p.wcId}
              src={p.image}
              alt=""
              className={`absolute inset-0 w-full h-full object-contain ${isUnavailable ? 'opacity-40' : ''}`}
              style={{ zIndex: p.layer }}
            />
          ))}

          {/* Unavailable overlay */}
          {isUnavailable && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/30">
              <div className="text-center px-1">
                <p className="text-[9px] font-bold text-white leading-tight drop-shadow">
                  {ct('popular')}
                </p>
                <p className="text-[8px] text-white/90 drop-shadow">
                  {ct('outOfStock')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDelete(item.id)
          }}
          className="absolute -top-1 -right-1 z-50 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
        >
          ×
        </button>

        {/* Price */}
        <p className={`text-xs text-center mt-1 ${isUnavailable ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
          {(item.total_price / 100).toFixed(0)}€
        </p>
      </div>
    )
  }

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-40 lg:hidden bg-white rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)] flex flex-col animate-in slide-in-from-bottom duration-300"
      style={{ top: topPosition }}
    >
      {/* Header with drag indicator */}
      <div className="flex flex-col items-center pt-2 pb-3 border-b">
        {/* Drag indicator */}
        <div className="w-10 h-1 bg-gray-300 rounded-full mb-2" />
        <div className="flex items-center justify-between w-full px-4">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            {ct('myWishlist')}
            <span className="text-sm font-normal text-gray-500">({wishlists.length}/{maxItems})</span>
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Grid of wishlist items */}
      <div className="flex-1 overflow-y-auto p-3">
        {/* Available items */}
        <div className="grid grid-cols-3 gap-2">
          {availableWishlists.map(item => renderItem(item, false))}
        </div>

        {/* Unavailable section */}
        {unavailableWishlists.length > 0 && (
          <>
            {availableWishlists.length > 0 && (
              <div className="my-3 border-t border-gray-200" />
            )}
            <p className="text-xs text-gray-400 text-center font-medium uppercase tracking-wide mb-2">
              {ct('outOfStock')}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {unavailableWishlists.map(item => renderItem(item, true))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================
// WISHLIST FULL POPUP COMPONENT
// ============================================
interface WishlistFullPopupProps {
  count: number
  max: number
  onClose: () => void
}

function WishlistFullPopup({ count, max, onClose }: WishlistFullPopupProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
          {ct('wishlistFull')}
        </h2>

        {/* Description */}
        <p className="text-gray-600 text-center mb-4">
          {ct('wishlistFullText', { max })}
        </p>

        {/* Counter */}
        <div className="text-center mb-6">
          <span className="inline-block px-4 py-2 bg-pink-50 text-pink-600 font-semibold rounded-full">
            {count}/{max}
          </span>
        </div>

        {/* Button */}
        <button
          onClick={onClose}
          className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white font-medium rounded-lg transition-colors"
        >
          {ct('gotIt')}
        </button>
      </div>
    </div>
  )
}
